import { formatMoney } from '../../shared/format/public-api';

export type PreCuentaLine = {
  name: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  optionsLabel?: string | null;
  lineNote?: string | null;
};

/** Mapea líneas de carrito / cuenta de mesa al shape de precuenta. */
export function mapCartLikeToPreCuentaLines(
  lines: Array<{
    name: string;
    quantity?: number;
    qty?: number;
    price?: number;
    unitPrice?: number;
    optionsLabel?: string | null;
    lineNote?: string | null;
  }>
): PreCuentaLine[] {
  return lines.map((line) => {
    const qty = Number(line.quantity ?? line.qty ?? 0) || 0;
    const unitPrice = Number(line.unitPrice ?? line.price ?? 0) || 0;
    return {
      name: (line.name ?? '').trim() || 'Producto',
      qty,
      unitPrice,
      lineTotal: unitPrice * qty,
      optionsLabel: line.optionsLabel?.trim() || null,
      lineNote: line.lineNote?.trim() || null,
    };
  });
}

function money(n: number): string {
  return `${formatMoney(Math.round(n))}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function whenLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

export type PreCuentaContext = 'mesa' | 'counter';

/** Cuenta / resumen no fiscal (print). 58–80 mm. */
export function buildPreCuenta(args: {
  tableName: string;
  /** Comensales de la sesión (`table_sessions.covers`), no capacity del plano. */
  covers?: number | null;
  lines: PreCuentaLine[];
  subtotal: number;
  discountTotal?: number;
  /** Propina sugerida / intent; se imprime si > 0. Total lo arma el caller (mercadería + tip). */
  tipAmount?: number;
  total: number;
  openedAt?: string | null;
  /** counter = mostrador QSR (G6); mesa = salón. */
  context?: PreCuentaContext;
}): { text: string; html: string } {
  const context = args.context ?? 'mesa';
  const isCounter = context === 'counter';
  const title = isCounter ? 'Resumen del pedido' : 'PRECUENTA';
  const subtitle = isCounter ? 'Revisá antes de cobrar' : '(no fiscal)';
  const sep = '----------------';
  const when = whenLabel(args.openedAt);
  const discount = Math.max(0, args.discountTotal ?? 0);
  const tip = Math.max(0, Number(args.tipAmount ?? 0) || 0);
  const coversN =
    !isCounter && args.covers != null && Number(args.covers) > 0
      ? Math.max(1, Math.round(Number(args.covers)))
      : null;

  const textLines: (string | null)[] = [
    isCounter ? `  ${title}` : `      ${title}`,
    subtitle,
    isCounter ? null : `Mesa: ${args.tableName}`,
    coversN != null ? `Cubiertos: ${coversN}` : null,
    when ? `Abierta: ${when}` : null,
    sep,
    ...args.lines.flatMap((line) => {
      const left = `${line.qty}× ${line.name}`;
      const right = money(line.lineTotal);
      const pad = Math.max(1, 28 - left.length - right.length);
      const opts = line.optionsLabel?.trim() || '';
      const note = line.lineNote?.trim() || '';
      return [
        `${left}${' '.repeat(pad)}${right}`,
        opts ? `  ${opts}` : null,
        note ? `  Nota: ${note}` : null,
      ];
    }),
    sep,
    `Subtotal ${money(args.subtotal)}`,
    discount > 0 ? `Descuento −${money(discount)}` : null,
    tip > 0 ? `Propina ${money(tip)}` : null,
    `Total ${money(args.total)}`,
  ];

  const text = textLines.filter((l): l is string => l != null && l !== '').join('\n');

  const itemRowsHtml = args.lines
    .map((line) => {
      const opts = line.optionsLabel?.trim() || '';
      const note = line.lineNote?.trim() || '';
      const meta = [
        opts ? `<div class="mod">${escapeHtml(opts)}</div>` : '',
        note ? `<div class="mod">Nota: ${escapeHtml(note)}</div>` : '',
      ].join('');
      return `<tr><td class="qty">${escapeHtml(String(line.qty))}×</td><td class="name">${escapeHtml(line.name)}${meta}</td><td class="price">${money(line.lineTotal)}</td></tr>`;
    })
    .join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>
  @page { margin: 4mm; size: 72mm auto; }
  @media print { body { margin: 0; } }
  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px; color: #111; margin: 0; padding: 6px;
    max-width: 72mm; width: 72mm; box-sizing: border-box;
  }
  .title {
    text-align: center; font-size: 22px; font-weight: 800;
    letter-spacing: 0.12em; line-height: 1.1; margin: 4px 0 2px;
  }
  .center { text-align: center; }
  .meta { margin: 2px 0; }
  .sep { border: none; border-top: 1px solid #111; margin: 8px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { vertical-align: top; padding: 2px 0; }
  td.qty { width: 2.2em; white-space: nowrap; }
  td.name { word-break: break-word; }
  td.name .mod { font-size: 11px; margin-top: 1px; }
  td.price { text-align: right; white-space: nowrap; padding-left: 6px; }
  .row { margin: 2px 0; }
  .total { font-weight: 800; font-size: 14px; margin-top: 4px; }
</style></head><body>
  <div class="title">${escapeHtml(title)}</div>
  <div class="center meta">${escapeHtml(subtitle)}</div>
  ${isCounter ? '' : `<div class="meta"><strong>Mesa: ${escapeHtml(args.tableName)}</strong></div>`}
  ${coversN != null ? `<div class="meta">Cubiertos: ${coversN}</div>` : ''}
  ${when ? `<div class="meta">Abierta: ${escapeHtml(when)}</div>` : ''}
  <hr class="sep"/>
  <table>${itemRowsHtml || '<tr><td>(sin ítems)</td></tr>'}</table>
  <hr class="sep"/>
  <div class="row">Subtotal ${money(args.subtotal)}</div>
  ${discount > 0 ? `<div class="row">Descuento −${money(discount)}</div>` : ''}
  ${tip > 0 ? `<div class="row">Propina ${money(tip)}</div>` : ''}
  <div class="total">Total ${money(args.total)}</div>
</body></html>`;

  return { text, html };
}
